import { Colors, Spinner } from '@amelio/ui-library';
import { useState } from 'react';

import { AspirationResults } from './AspirationResults';
import { CareerJourneyNotes } from './CareerJourneyNotes';
import { CareerObjectiveCard } from './CareerObjectiveCard';
import { CareerPathTimeline } from './CareerPathTimeline';
import { CareerStepsEditorModal } from './CareerStepsEditorModal';
import { hasGrowthContent } from './growthContent';
import { GrowthEmptyInvitation } from './GrowthEmptyInvitation';
import { GrowthForbiddenNotice } from './GrowthForbiddenNotice';
import { GrowthPerformanceScore } from './GrowthPerformanceScore';
import { NextTargetCard } from './NextTargetCard';

import type { CareerStepKind } from '@/types/career-growth';
import type { User } from '@/types/directory/User';
import type { PerformanceScore } from '@/types/performance-profile';

import { isRefusedError } from '@/api/api';
import { isValidProfileId } from '@/api/competency-services/profile-assignment/profileAssignmentService';
import {
    useGetEmployeeCompetencyAssignment,
    useGetEmployeeGrowth,
    useGetProficiencyLevels,
} from '@/api/performance-profile';
import { useAmelioTranslations, useNavigate } from '@/hooks';
import { meetingPlanPath } from '@/navigation/thematicMeetingsRoutes';
import { RouteName } from '@/navigation/types';
import { toNewMeetingLocationState } from '@/types/thematic-meetings/newMeetingPrefill';
import { getLocalizedText } from '@/utils/localization';

interface GrowthTabProps {
    employeeId: string;
    /** The employee whose profile is open; `undefined` while the directory answers. */
    employee?: User;
    /** The score the page already loaded for the header, reused by the score section. */
    scoreData: PerformanceScore;
    /** Whether the reader may see the career journey notes; off on one's own profile. */
    canReadNotes: boolean;
    /** Opens the page's competency-profile assignment flow. */
    onEditCurrentProfile: () => void;
    /** Opens the page's screen for the target position and the competency profile it requires. */
    onEditTargetProfile: () => void;
    /** The period the score covers; the filter that set it lives on the Évaluation tab. */
    scorePeriodLabel: string | null;
}

/**
 * The "Croissance" tab of the performance profile: the career objective next to the career path, the next target next to the performance score, the
 * aspiration assessment results, then the career journey notes. Everything comes from
 * `GET …/growth` plus the profile assignment the page already fetches; the notes section pages
 * itself.
 *
 * With nothing recorded at all, the six sections give way to a single invitation: six boxes each
 * stating its own absence say less than one sentence saying where to start. The invitation carries
 * the two ways out that create the very first fact, and one single fact anywhere brings all six
 * sections back at once -- so no creation button is ever hidden behind a tab that already has
 * something in it.
 */
export const GrowthTab = ({
    employeeId,
    employee,
    scoreData,
    canReadNotes,
    onEditCurrentProfile,
    onEditTargetProfile,
    scorePeriodLabel,
}: GrowthTabProps) => {
    const { t } = useAmelioTranslations();
    const navigate = useNavigate();

    const {
        data: growth,
        dataUpdatedAt,
        isLoading,
        isError,
        error,
    } = useGetEmployeeGrowth(employeeId);
    // The assignment decides, on its own, whether the tab has anything to show: read before it
    // answers, its absence would be indistinguishable from an employee with no profile, and the
    // invitation would flash over a tab that is not empty.
    const {
        data: assignment,
        isLoading: isAssignmentLoading,
        isError: isAssignmentInError,
    } = useGetEmployeeCompetencyAssignment(employeeId);
    const { data: proficiencyLevels, isError: areLevelsInError } = useGetProficiencyLevels();

    const [stepsEditor, setStepsEditor] = useState<{
        isOpen: boolean;
        initialEditKind: CareerStepKind | null;
        // Bumped on every opening only, so the editor remounts with a clean initial state each
        // time it opens, without touching its own mount while it plays its closing animation.
        openKey: number;
    }>({ isOpen: false, initialEditKind: null, openKey: 0 });

    const openStepsEditor = (initialEditKind: CareerStepKind | null = null) =>
        setStepsEditor((current) => ({
            isOpen: true,
            initialEditKind,
            openKey: current.openKey + 1,
        }));
    const closeStepsEditor = () =>
        setStepsEditor((current) => ({ ...current, isOpen: false, initialEditKind: null }));

    const openMeeting = (meetingId: string) => navigate({ path: meetingPlanPath(meetingId) });

    /**
     * "Réévaluer" hands over to the meetings page's planning wizard, opened on this employee with
     * the "Aspirations de carrière" topic already picked: the manager still chooses the date and
     * the duration there, and the meeting only exists once the wizard is submitted.
     */
    const handleReEvaluate = () =>
        navigate({
            path: RouteName.ThematicMeetings,
            state: toNewMeetingLocationState({ employeeId, topic: 'Career' }),
        });

    if (isLoading || isAssignmentLoading) {
        return (
            <div
                className={'flex items-center justify-center p-8'}
                data-testid={'growth-tab-loading'}
            >
                <Spinner
                    size={'md'}
                    color={Colors.PRIMARY}
                />
            </div>
        );
    }

    if (isError || !growth) {
        // The tab only ever reads an employee who is not the reader -- the page decides that much
        // before mounting it -- so a refusal here means the reader does not manage this employee:
        // a former one, whose profile stays open to HR while this tab does not. That is a rule
        // rather than a fault, and only a genuine failure keeps the error colour.
        if (isRefusedError(error)) {
            return <GrowthForbiddenNotice reason={'outOfScope'} />;
        }

        return (
            <div
                className={'flex items-center justify-center p-8'}
                data-testid={'growth-tab-error'}
            >
                <p className={'text-error'}>{t('performanceProfile.error')}</p>
            </div>
        );
    }

    const employeeFirstName = employee?.firstName ?? '';
    // An assignment whose profile has no name in any language reads as "undefined", like no
    // assignment at all, rather than as an empty value next to its label.
    const currentProfileName = getLocalizedText(assignment?.profileName) || null;
    // An employee without a profile still gets an assignment back, carrying the empty GUID: sent
    // with a current position, that id is a profile the API rightly says does not exist.
    const currentProfile =
        assignment && isValidProfileId(assignment.competencyProfileId)
            ? { id: assignment.competencyProfileId, name: currentProfileName ?? '' }
            : null;
    // A profile counts by its assignment, not by its name: one named in no language still fills the
    // "Prochaine cible" card, which would disappear behind the invitation if the name decided.
    // An assignment the request failed to read is not an absence of assignment: concluding "empty"
    // there would hide a tab that has content, so the sections stay and "Prochaine cible" says on
    // its own that it is undefined.
    if (
        !isAssignmentInError &&
        !hasGrowthContent({
            growth,
            hasCompetencyProfile: currentProfile !== null,
            canReadNotes,
        })
    ) {
        return (
            <GrowthEmptyInvitation
                employeeId={employeeId}
                employeeFirstName={employeeFirstName}
                onPlanMeeting={handleReEvaluate}
            />
        );
    }

    const currentStep = growth.careerSteps.find((step) => step.kind === 'Current') ?? null;

    return (
        <div
            className={'flex flex-col gap-8 md:gap-16'}
            data-testid={'growth-tab'}
        >
            {/* The objective card takes a quarter of the row, the career path the rest. */}
            <div
                className={
                    'grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(17rem,1fr)_minmax(0,3fr)] lg:gap-14'
                }
            >
                <CareerObjectiveCard
                    targetPosition={growth.targetPosition}
                    aspiration={growth.currentAspiration}
                    employeeFirstName={employeeFirstName}
                    onReEvaluate={handleReEvaluate}
                />
                <CareerPathTimeline
                    steps={growth.careerSteps}
                    onEditSteps={() => openStepsEditor()}
                />
            </div>

            {/* The next target takes two thirds of the row, the score the remaining third. */}
            <div className={'grid items-start gap-6 lg:grid-cols-[2fr_1fr] lg:gap-8'}>
                <NextTargetCard
                    currentStep={currentStep}
                    currentProfileName={currentProfileName}
                    targetPosition={growth.targetPosition}
                    nextLevelSkills={growth.nextLevelSkills}
                    proficiencyLevels={proficiencyLevels}
                    areLevelsInError={areLevelsInError}
                    employeeFirstName={employeeFirstName}
                    onEditCurrentStep={() => openStepsEditor('Current')}
                    onEditCurrentProfile={onEditCurrentProfile}
                    onEditTargetProfile={onEditTargetProfile}
                />
                <GrowthPerformanceScore
                    score={scoreData}
                    currentProfileName={currentProfileName}
                    onEditCurrentProfile={onEditCurrentProfile}
                    scorePeriodLabel={scorePeriodLabel}
                />
            </div>

            <AspirationResults
                onPlanCareerMeeting={handleReEvaluate}
                employeeId={employeeId}
                employeeFirstName={employeeFirstName}
                currentAspiration={growth.currentAspiration}
                history={growth.aspirationHistory}
                onOpenMeeting={openMeeting}
            />

            {canReadNotes && (
                <CareerJourneyNotes
                    employeeId={employeeId}
                    initialNotes={{ page: growth.notes, updatedAt: dataUpdatedAt }}
                />
            )}

            <CareerStepsEditorModal
                key={stepsEditor.openKey}
                isOpen={stepsEditor.isOpen}
                onClose={closeStepsEditor}
                employeeId={employeeId}
                steps={growth.careerSteps}
                currentProfile={currentProfile}
                initialEditKind={stepsEditor.initialEditKind}
            />
        </div>
    );
};
